import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { PlusIcon, XIcon, BuildingIcon, UserIcon, FileTextIcon, UserCheckIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { AddressAutocomplete } from '../components/AddressAutocomplete'
import { LeadManagementFields } from '../components/LeadManagementFields'
import { ConvertLeadModal } from '../components/ConvertLeadModal'
import { logActivity, ActivityTypes } from '../utils/activityLogger'
import type { LeadStage } from '../lib/leadHelpers'
import { contactHasName, normalizeContactFields, getContactDisplayName, serializeLeadProjects, normalizeAssignedToId } from '../lib/leadHelpers'
import { stripInternalConversionCustomFields } from '../lib/leadConversionForm'
import { BUILDING_TYPES } from '../components/property/types'
import { buildUpdateDescription, diffSnapshots } from '../lib/activityChangeSummary'

interface LeadSource {
  id: string
  name: string
}

interface UserProfile {
  id: string
  first_name?: string
  last_name?: string
  email: string
}

interface Contact {
  id: string
  first_name: string
  last_name: string
  name?: string
  title?: string
  phone: string
  extension?: string
  cell: string
  email: string
}

interface Project {
  id: string
  type: string
  address: string
  address_line_2?: string
  city: string
  state: string
  zip: string
  unit_count: string
  work_type: string
  notes: string
}

export default function LeadEdit() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [leadSources, setLeadSources] = useState<LeadSource[]>([])
  const [users, setUsers] = useState<UserProfile[]>([])
  const [serviceCategories, setServiceCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'company' | 'contacts' | 'projects' | 'notes'>(
    'company'
  )
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())

  // Company Information
  const [companyData, setCompanyData] = useState({
    name: '',
    address: '',
    address_line_2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    website: '',
  })

  // Contact Information (array of contacts)
  const [contacts, setContacts] = useState<Contact[]>([
    { id: '1', first_name: '', last_name: '', title: '', phone: '', extension: '', cell: '', email: '' },
  ])

  // Projects (array of projects)
  const [projects, setProjects] = useState<Project[]>([
    {
      id: '1',
      type: '',
      address: '',
      address_line_2: '',
      city: '',
      state: '',
      zip: '',
      unit_count: '',
      work_type: '',
      notes: '',
    },
  ])

  // Lead Management
  const [leadData, setLeadData] = useState({
    notes: '',
    lead_source_id: '',
    lead_source_other: '',
    assigned_to: '',
    next_activity_date: '',
    stage: 'new' as LeadStage,
  })

  // Lead Notes (from lead_notes table)
  const [leadNotes, setLeadNotes] = useState<Array<{ id: string; note: string; created_at: string; created_by_name?: string }>>([])
  const [newNote, setNewNote] = useState('')
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error'
    customerId?: string
  } | null>(null)
  const [showConvertModal, setShowConvertModal] = useState(false)
  const [convertedToCustomerId, setConvertedToCustomerId] = useState<string | null>(null)
  const [customFields, setCustomFields] = useState<Record<string, any>>({})
  const [newCustomFieldKey, setNewCustomFieldKey] = useState('')
  const [newCustomFieldValue, setNewCustomFieldValue] = useState('')
  const initialSnapshotRef = useRef<Record<string, unknown> | null>(null)

  const LEAD_UPDATE_LABELS: Record<string, string> = {
    company_name: 'Company Name',
    company_address: 'Address',
    company_city: 'City',
    company_state: 'State',
    company_zip: 'Zip',
    company_phone: 'Phone',
    company_email: 'Email',
    company_website: 'Website',
    stage: 'Stage',
    assigned_to: 'Assigned To',
    lead_source: 'Lead Source',
    next_activity_date: 'Next Activity',
    contacts_count: 'Contacts',
    projects_count: 'Projects',
  }

  const resolveUserLabel = (userId: string) => {
    if (!userId) return '(empty)'
    const user = users.find((u) => u.id === userId)
    if (!user) return userId
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
  }

  const resolveLeadSourceLabel = (sourceId: string, sourceOther: string) => {
    if (sourceOther?.trim()) return sourceOther.trim()
    if (!sourceId) return '(empty)'
    const source = leadSources.find((s) => s.id === sourceId)
    return source?.name || sourceId
  }

  const buildLeadSnapshot = (
    company: typeof companyData,
    lead: typeof leadData,
    contactsList: Contact[],
    projectsList: Project[]
  ): Record<string, unknown> => ({
    company_name: company.name,
    company_address: company.address,
    company_city: company.city,
    company_state: company.state,
    company_zip: company.zip,
    company_phone: company.phone,
    company_email: company.email,
    company_website: company.website,
    stage: lead.stage,
    assigned_to: lead.assigned_to || '',
    lead_source: resolveLeadSourceLabel(lead.lead_source_id, lead.lead_source_other),
    next_activity_date: lead.next_activity_date || '',
    contacts_count: contactsList.filter((c) => contactHasName(c)).length,
    projects_count: serializeLeadProjects(projectsList).length,
  })

  const withLeadDisplayLabels = (snapshot: Record<string, unknown>) => ({
    ...snapshot,
    assigned_to: resolveUserLabel(String(snapshot.assigned_to || '')) || '(empty)',
  })

  useEffect(() => {
    fetchLeadSources()
    fetchUsers()
    fetchServiceCategories()
    if (id) {
      fetchLead()
    }
  }, [id])

  const fetchLead = async () => {
    try {
      const { data, error } = await supabase.from('leads').select('*').eq('id', id).single()
      if (error) throw error
      if (data) {
        setCompanyData({
          name: data.company_name || '',
          address: data.company_address || '',
          address_line_2: data.company_address_line_2 || '',
          city: data.company_city || '',
          state: data.company_state || '',
          zip: data.company_zip || '',
          phone: data.company_phone || '',
          email: data.company_email || '',
          website: data.company_website || '',
        })
        // Load contacts, handling backwards compatibility with first_name/last_name fields
        const loadedContacts = data.contacts || []
        const normalizedContacts = loadedContacts.map((c: any) => {
          const names = normalizeContactFields(c)
          return {
            ...c,
            first_name: names.first_name,
            last_name: names.last_name,
            name: names.name,
            title: c.title || '',
          }
        })
        setContacts(
          normalizedContacts.length > 0
            ? normalizedContacts
            : [{ id: '1', first_name: '', last_name: '', title: '', phone: '', extension: '', cell: '', email: '' }]
        )
        setProjects(
          data.projects || [
            {
              id: '1',
              type: '',
              address: '',
              address_line_2: '',
              city: '',
              state: '',
              zip: '',
              unit_count: '',
              work_type: '',
              notes: '',
            },
          ]
        )
        const loadedProjects =
          data.projects && data.projects.length > 0
            ? data.projects
            : [{ id: '1' }]
        setExpandedProjects(new Set(loadedProjects.map((p: Project) => p.id)))
        setLeadData({
          notes: '',
          lead_source_id: data.lead_source_id || '',
          lead_source_other: data.lead_source_other || '',
          assigned_to: data.assigned_to || '',
          next_activity_date: data.next_activity_date || '',
          stage: (data.stage || 'new') as LeadStage,
        })

        // Load custom fields (hide internal conversion keys)
        if (data.custom_fields && typeof data.custom_fields === 'object') {
          setCustomFields(stripInternalConversionCustomFields(data.custom_fields))
        }
        setConvertedToCustomerId(data.converted_to_customer_id || null)

        const loadedProjectsList =
          data.projects && data.projects.length > 0 ? data.projects : []
        initialSnapshotRef.current = buildLeadSnapshot(
          {
            name: data.company_name || '',
            address: data.company_address || '',
            address_line_2: data.company_address_line_2 || '',
            city: data.company_city || '',
            state: data.company_state || '',
            zip: data.company_zip || '',
            phone: data.company_phone || '',
            email: data.company_email || '',
            website: data.company_website || '',
          },
          {
            notes: '',
            lead_source_id: data.lead_source_id || '',
            lead_source_other: data.lead_source_other || '',
            assigned_to: data.assigned_to || '',
            next_activity_date: data.next_activity_date || '',
            stage: (data.stage || 'new') as LeadStage,
          },
          normalizedContacts,
          loadedProjectsList
        )
      }

      // Fetch notes from lead_notes table
      const { data: notesData, error: notesError } = await supabase
        .from('lead_notes')
        .select('id, note, created_at, created_by_name')
        .eq('lead_id', id)
        .order('created_at', { ascending: false })

      if (!notesError && notesData) {
        setLeadNotes(notesData)
      }
    } catch (error) {
      console.error('Error fetching lead:', error)
    }
  }

  const fetchLeadSources = async () => {
    try {
      const { data, error } = await supabase
        .from('lead_sources')
        .select('id, name')
        .eq('is_active', true)
        .order('name')

      if (error) throw error
      setLeadSources(data || [])
    } catch (error) {
      console.error('Error fetching lead sources:', error)
    }
  }

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email')
        .order('first_name')

      if (error) throw error
      setUsers(data || [])
    } catch (error) {
      console.error('Error fetching users:', error)
    }
  }

  const fetchServiceCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('id, name')
        .order('name')

      if (error) throw error
      setServiceCategories(data || [])
    } catch (error) {
      console.error('Error fetching service categories:', error)
    }
  }

  const addContact = () => {
    const newContact: Contact = {
      id: Date.now().toString(),
      first_name: '',
      last_name: '',
      phone: '',
      extension: '',
      cell: '',
      email: '',
    }
    setContacts([...contacts, newContact])
  }

  const removeContact = (id: string) => {
    setContacts(contacts.filter((c) => c.id !== id))
  }

  const updateContact = (id: string, field: keyof Contact, value: string) => {
    setContacts(contacts.map((c) => (c.id === id ? { ...c, [field]: value } : c)))
  }

  const addProject = () => {
    const newId = Date.now().toString()
    const newProject: Project = {
      id: newId,
      type: '',
      address: '',
      address_line_2: '',
      city: '',
      state: '',
      zip: '',
      unit_count: '',
      work_type: '',
      notes: '',
    }
    setProjects([...projects, newProject])
    setExpandedProjects((prev) => new Set([...Array.from(prev), newId]))
  }

  const removeProject = (id: string) => {
    setProjects(projects.filter((p) => p.id !== id))
  }

  const updateProject = (id: string, field: keyof Project, value: string) => {
    setProjects(projects.map((p) => (p.id === id ? { ...p, [field]: value } : p)))
  }

  const updateProjectAddress = (
    id: string,
    addressData: { address: string; city: string; state: string; zip: string }
  ) => {
    setProjects(
      projects.map((p) =>
        p.id === id
          ? {
              ...p,
              address: addressData.address,
              city: addressData.city,
              state: addressData.state,
              zip: addressData.zip,
            }
          : p
      )
    )
  }

  const addNote = async () => {
    if (newNote.trim() && id) {
      try {
        const { data, error } = await supabase
          .from('lead_notes')
          .insert({
            lead_id: id,
            note: newNote.trim(),
            note_type: 'general',
          })
          .select()
          .single()

        if (error) throw error

        if (data) {
          setLeadNotes([...leadNotes, {
            id: data.id,
            note: data.note,
            created_at: data.created_at,
            created_by_name: data.created_by_name || undefined,
          }])
          setNewNote('')
        }
      } catch (error) {
        console.error('Error adding note:', error)
        // Fallback to local state if table insert fails
        setLeadNotes([...leadNotes, {
          id: `temp_${Date.now()}`,
          note: newNote.trim(),
          created_at: new Date().toISOString(),
        }])
        setNewNote('')
      }
    }
  }

  const removeNote = async (noteId: string) => {
    if (id && !noteId.startsWith('temp_')) {
      try {
        const { error } = await supabase
          .from('lead_notes')
          .delete()
          .eq('id', noteId)
          .eq('lead_id', id)

        if (error) throw error
      } catch (error) {
        console.error('Error removing note:', error)
      }
    }
    setLeadNotes(leadNotes.filter((n) => n.id !== noteId))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Get primary contact if exists
      const primaryContact = contacts[0] || null

      // Build update object conditionally to avoid errors if column doesn't exist yet
      const updateData: any = {
        // Company Information
        company_name: companyData.name || null,
        company_address: companyData.address || null,
        company_city: companyData.city || null,
        company_state: companyData.state || null,
        company_zip: companyData.zip || null,
        company_phone: companyData.phone || null,
        company_email: companyData.email || null,
        company_website: companyData.website || null,
      }

      // Only include address_line_2 if it has a value (column may not exist in DB yet)
      if (companyData.address_line_2) {
        updateData.company_address_line_2 = companyData.address_line_2
      }

      const primaryNames = primaryContact ? normalizeContactFields(primaryContact) : null
      const contactsData = contacts
        .filter((c) => contactHasName(c))
        .map((c) => {
          const names = normalizeContactFields(c)
          return { ...c, ...names }
        })

      const { error } = await supabase
        .from('leads')
        .update({
          ...updateData,

          // Contact Information (primary contact in legacy fields)
          contact_first_name: primaryNames?.first_name || '',
          contact_last_name: primaryNames?.last_name || '',
          phone: primaryContact?.phone || null,
          email: primaryContact?.email || null,

          // New Structured Data Fields
          contacts: contactsData.length > 0 ? contactsData : [],
          projects: serializeLeadProjects(projects),

          // Lead Management
          lead_source_id: leadData.lead_source_id || null,
          lead_source_other: leadData.lead_source_other?.trim() || null,
          assigned_to: normalizeAssignedToId(leadData.assigned_to),
          next_activity_date: leadData.next_activity_date || null,
          stage: leadData.stage || 'new',

          // Custom Fields
          custom_fields: (() => {
            const cleaned = stripInternalConversionCustomFields(customFields)
            return Object.keys(cleaned).length > 0 ? cleaned : null
          })(),
        })
        .eq('id', id)

      if (error) throw error

      // Handle notes - save new notes to lead_notes table
      // Note: We'll handle note deletion separately in the UI
      // For now, we just ensure notes are saved when the form is submitted
      // (Notes are managed separately via addNote/removeNote functions)

      // Log activity
      if (id) {
        const currentSnapshot = buildLeadSnapshot(companyData, leadData, contacts, projects)
        const changes = diffSnapshots(
          withLeadDisplayLabels(initialSnapshotRef.current || {}),
          withLeadDisplayLabels(currentSnapshot),
          LEAD_UPDATE_LABELS
        )

        await logActivity({
          activity_type: ActivityTypes.LEAD_UPDATED,
          entity_type: 'lead',
          entity_id: id,
          description: buildUpdateDescription(`Lead "${companyData.name || 'Unnamed'}"`, changes),
          metadata: { changes },
        })

        initialSnapshotRef.current = currentSnapshot
      }

      setNotification({
        type: 'success',
        message: 'Lead updated successfully!',
      })
      setTimeout(() => {
        navigate('/leads')
      }, 1500)
    } catch (error) {
      console.error('Error updating lead:', error)
      setNotification({
        type: 'error',
        message: 'Error updating lead: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setLoading(false)
    }
  }

  const projectTypes = BUILDING_TYPES

  const buildConvertLeadSnapshot = () => {
    const primary = contacts[0]
    const primaryNames = primary ? normalizeContactFields(primary) : { first_name: '', last_name: '' }
    const contactsData = contacts
      .filter((c) => contactHasName(c))
      .map((c) => ({ ...c, ...normalizeContactFields(c) }))
    return {
      id,
      company_name: companyData.name,
      company_address: companyData.address,
      company_address_line_2: companyData.address_line_2,
      company_city: companyData.city,
      company_state: companyData.state,
      company_zip: companyData.zip,
      company_phone: companyData.phone,
      company_email: companyData.email,
      company_website: companyData.website,
      contact_first_name: primaryNames.first_name,
      contact_last_name: primaryNames.last_name,
      contacts: contactsData,
      projects,
      converted_to_customer_id: convertedToCustomerId,
    }
  }

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title="Edit Lead"
        onBack={() => navigate('/leads')}
        backLabel="Back to Leads"
        actions={
          !convertedToCustomerId ? (
            <button
              type="button"
              onClick={() => setShowConvertModal(true)}
              className={`${mobile.successBtn} px-3 text-xs sm:text-sm`}
            >
              <UserCheckIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Convert</span>
            </button>
          ) : undefined
        }
      />

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tab Navigation */}
          <div className={mobile.surface}>
            <div className="sticky top-[3.25rem] z-10 flex overflow-x-auto scrollbar-none border-b border-gray-200 bg-white/95 backdrop-blur-md">
              <button
                type="button"
                onClick={() => setActiveTab('company')}
                className={`flex-1 min-w-[5.5rem] px-4 sm:px-5 min-h-[48px] flex items-center justify-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'company'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Company
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('contacts')}
                className={`flex-1 min-w-[5.5rem] px-4 sm:px-5 min-h-[48px] flex items-center justify-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'contacts'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Contacts
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('projects')}
                className={`flex-1 min-w-[5.5rem] px-4 sm:px-5 min-h-[48px] flex items-center justify-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'projects'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Projects
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('notes')}
                className={`flex-1 min-w-[5.5rem] px-4 sm:px-5 min-h-[48px] flex items-center justify-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'notes'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Notes
              </button>
            </div>
            <div className="p-4 sm:p-6">
              {/* Company Tab */}
              {activeTab === 'company' && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <BuildingIcon className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Company Information</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={companyData.name}
                        onChange={(e) => setCompanyData({ ...companyData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        value={companyData.phone}
                        onChange={(e) => {
                          const formatted = e.target.value.replace(/\D/g, '').slice(0, 10)
                          const display =
                            formatted.length > 0
                              ? formatted.length <= 3
                                ? formatted
                                : formatted.length <= 6
                                ? `(${formatted.slice(0, 3)}) ${formatted.slice(3)}`
                                : `(${formatted.slice(0, 3)}) ${formatted.slice(
                                    3,
                                    6
                                  )}-${formatted.slice(6)}`
                              : ''
                          setCompanyData({ ...companyData, phone: display })
                        }}
                        placeholder="(123) 456-7890"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={companyData.email}
                        onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Website
                      </label>
                      <input
                        type="text"
                        value={companyData.website}
                        onChange={(e) =>
                          setCompanyData({ ...companyData, website: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="www.example.com or example.com"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Address
                      </label>
                      <AddressAutocomplete
                        value={companyData.address}
                        onChange={(address) => setCompanyData({ ...companyData, address })}
                        onAddressSelect={(addressData) =>
                          setCompanyData({
                            ...companyData,
                            address: addressData.address,
                            city: addressData.city,
                            state: addressData.state,
                            zip: addressData.zip,
                          })
                        }
                        placeholder="Start typing address..."
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Address Line 2 <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        value={companyData.address_line_2}
                        onChange={(e) =>
                          setCompanyData({ ...companyData, address_line_2: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Office #, Suite, Unit, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={companyData.city}
                        onChange={(e) => setCompanyData({ ...companyData, city: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                      <input
                        type="text"
                        value={companyData.state}
                        onChange={(e) => setCompanyData({ ...companyData, state: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="NY"
                        maxLength={2}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Zip Code
                      </label>
                      <input
                        type="text"
                        value={companyData.zip}
                        onChange={(e) => setCompanyData({ ...companyData, zip: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="10001"
                      />
                    </div>
                  </div>

                  {/* Custom Fields Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Fields</h3>
                    <div className="space-y-3">
                      {Object.entries(customFields).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              {key}
                            </label>
                            <input
                              type="text"
                              value={value}
                              onChange={(e) =>
                                setCustomFields({ ...customFields, [key]: e.target.value })
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newFields = { ...customFields }
                              delete newFields[key]
                              setCustomFields(newFields)
                            }}
                            className="mt-6 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove field"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex items-end gap-2 pt-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Field Name
                          </label>
                          <input
                            type="text"
                            value={newCustomFieldKey}
                            onChange={(e) => setNewCustomFieldKey(e.target.value)}
                            placeholder="e.g., Industry, Size, etc."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Value
                          </label>
                          <input
                            type="text"
                            value={newCustomFieldValue}
                            onChange={(e) => setNewCustomFieldValue(e.target.value)}
                            placeholder="Field value"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (newCustomFieldKey.trim() && newCustomFieldValue.trim()) {
                              setCustomFields({
                                ...customFields,
                                [newCustomFieldKey.trim()]: newCustomFieldValue.trim(),
                              })
                              setNewCustomFieldKey('')
                              setNewCustomFieldValue('')
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Contacts Tab */}
              {activeTab === 'contacts' && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <UserIcon className="w-6 h-6 text-blue-600" />
                      <h2 className="text-xl font-semibold text-gray-900">Contact Information</h2>
                    </div>
                    <button
                      type="button"
                      onClick={addContact}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <PlusIcon className="w-5 h-5" />
                      Add Contact
                    </button>
                  </div>
                  <div className="space-y-4">
                    {contacts.map((contact, index) => (
                      <div key={contact.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-medium text-gray-900">
                            {getContactDisplayName(contact)} {index === 0 && '(Primary)'}
                          </h3>
                          {contacts.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeContact(contact.id)}
                              className="p-1 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <XIcon className="w-5 h-5 text-red-600" />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              First Name {index === 0 ? '*' : ''}
                            </label>
                            <input
                              type="text"
                              required={index === 0}
                              value={contact.first_name}
                              onChange={(e) => updateContact(contact.id, 'first_name', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Last Name {index === 0 ? '*' : ''}
                            </label>
                            <input
                              type="text"
                              required={index === 0}
                              value={contact.last_name}
                              onChange={(e) => updateContact(contact.id, 'last_name', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Title
                            </label>
                            <input
                              type="text"
                              value={contact.title || ''}
                              onChange={(e) => updateContact(contact.id, 'title', e.target.value)}
                              placeholder="e.g., Manager, Director, etc."
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email
                            </label>
                            <input
                              type="email"
                              value={contact.email}
                              onChange={(e) => updateContact(contact.id, 'email', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Phone
                              </label>
                              <input
                                type="tel"
                                value={contact.phone}
                                onChange={(e) => {
                                  const formatted = e.target.value.replace(/\D/g, '').slice(0, 10)
                                  const display =
                                    formatted.length > 0
                                      ? formatted.length <= 3
                                        ? formatted
                                        : formatted.length <= 6
                                        ? `(${formatted.slice(0, 3)}) ${formatted.slice(3)}`
                                        : `(${formatted.slice(0, 3)}) ${formatted.slice(
                                            3,
                                            6
                                          )}-${formatted.slice(6)}`
                                      : ''
                                  updateContact(contact.id, 'phone', display)
                                }}
                                placeholder="(123) 456-7890"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div className="w-24">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Ext. <span className="text-gray-400 font-normal">(Optional)</span>
                              </label>
                              <input
                                type="text"
                                value={contact.extension || ''}
                                onChange={(e) =>
                                  updateContact(contact.id, 'extension', e.target.value)
                                }
                                placeholder="Ext"
                                maxLength={10}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Cell
                            </label>
                            <input
                              type="tel"
                              value={contact.cell}
                              onChange={(e) => {
                                const formatted = e.target.value.replace(/\D/g, '').slice(0, 10)
                                const display =
                                  formatted.length > 0
                                    ? formatted.length <= 3
                                      ? formatted
                                      : formatted.length <= 6
                                      ? `(${formatted.slice(0, 3)}) ${formatted.slice(3)}`
                                      : `(${formatted.slice(0, 3)}) ${formatted.slice(
                                          3,
                                          6
                                        )}-${formatted.slice(6)}`
                                    : ''
                                updateContact(contact.id, 'cell', display)
                              }}
                              placeholder="(123) 456-7890"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Projects Tab */}
              {activeTab === 'projects' && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <FileTextIcon className="w-6 h-6 text-blue-600" />
                      <h2 className="text-xl font-semibold text-gray-900">Potential Projects</h2>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {projects.map((project, index) => (
                      <div key={project.id} className="border border-gray-200 rounded-lg">
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedProjects(
                              new Set(
                                expandedProjects.has(project.id)
                                  ? Array.from(expandedProjects).filter((id) => id !== project.id)
                                  : [...Array.from(expandedProjects), project.id]
                              )
                            )
                          }}
                          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <span className="text-lg font-medium text-gray-900">
                              Project {index + 1}
                            </span>
                            <span className="text-sm text-gray-600">
                              {project.address || 'No address'}
                            </span>
                          </div>
                          {projects.length > 1 && !expandedProjects.has(project.id) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                removeProject(project.id)
                              }}
                              className="p-1 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <XIcon className="w-5 h-5 text-red-600" />
                            </button>
                          )}
                        </button>
                        {expandedProjects.has(project.id) && (
                          <div className="border-t border-gray-200 p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Building Type
                                </label>
                                <select
                                  value={project.type}
                                  onChange={(e) =>
                                    updateProject(project.id, 'type', e.target.value)
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="">Select type</option>
                                  {projectTypes.map((type) => (
                                    <option key={type} value={type}>
                                      {type}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Unit Count
                                </label>
                                <input
                                  type="number"
                                  value={project.unit_count}
                                  onChange={(e) =>
                                    updateProject(project.id, 'unit_count', e.target.value)
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Address
                                </label>
                                <AddressAutocomplete
                                  key={`project-address-${project.id}`}
                                  value={project.address}
                                  onChange={(address) =>
                                    updateProject(project.id, 'address', address)
                                  }
                                  onAddressSelect={(addressData) =>
                                    updateProjectAddress(project.id, addressData)
                                  }
                                  placeholder="Start typing project address..."
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Apt/Suite #{' '}
                                  <span className="text-gray-400 font-normal">(Optional)</span>
                                </label>
                                <input
                                  type="text"
                                  value={project.address_line_2 || ''}
                                  onChange={(e) =>
                                    updateProject(project.id, 'address_line_2', e.target.value)
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Apartment, Suite, Unit, etc."
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  City
                                </label>
                                <input
                                  type="text"
                                  value={project.city}
                                  onChange={(e) =>
                                    updateProject(project.id, 'city', e.target.value)
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="City"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  State
                                </label>
                                <input
                                  type="text"
                                  value={project.state}
                                  onChange={(e) =>
                                    updateProject(project.id, 'state', e.target.value)
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="NY"
                                  maxLength={2}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Zip Code
                                </label>
                                <input
                                  type="text"
                                  value={project.zip}
                                  onChange={(e) => updateProject(project.id, 'zip', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="10001"
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Work Type
                                </label>
                                <select
                                  value={project.work_type}
                                  onChange={(e) =>
                                    updateProject(project.id, 'work_type', e.target.value)
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="">Select Work Type</option>
                                  {serviceCategories.map((category) => (
                                    <option key={category.id} value={category.id}>
                                      {category.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Project Notes
                                </label>
                                <textarea
                                  value={project.notes}
                                  onChange={(e) =>
                                    updateProject(project.id, 'notes', e.target.value)
                                  }
                                  rows={3}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              {projects.length > 1 && (
                                <div className="md:col-span-2">
                                  <button
                                    type="button"
                                    onClick={() => removeProject(project.id)}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                  >
                                    <XIcon className="w-5 h-5" />
                                    Remove Project
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addProject}
                      className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <PlusIcon className="w-5 h-5" />
                      Add Project
                    </button>
                    <p className="mt-3 text-sm text-gray-500">
                      Fill in at least an address or property type for each project, then click{' '}
                      <strong>Update Lead</strong> below to save.
                    </p>
                    <div className="mt-4 flex justify-end">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                      >
                        <PlusIcon className="w-5 h-5" />
                        {loading ? 'Updating...' : 'Update Lead'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes & Activity Tab */}
              {activeTab === 'notes' && (
                <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Lead Management</h2>
                    <LeadManagementFields
                      leadSources={leadSources}
                      users={users}
                      value={leadData}
                      onChange={(patch) => setLeadData({ ...leadData, ...patch })}
                      showStage
                    />
                  </div>

                  {/* Lead Notes Section */}
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Lead Notes</h2>
                    <div className="flex gap-2 mb-4">
                      <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) {
                            addNote()
                          }
                        }}
                        rows={3}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Add a note... (Ctrl+Enter to submit)"
                      />
                      <button
                        type="button"
                        onClick={addNote}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors self-start"
                      >
                        Add Note
                      </button>
                    </div>
                    <div className="space-y-3">
                      {leadNotes.length > 0 ? (
                        leadNotes
                          .slice()
                          .reverse()
                          .map((note) => (
                            <div
                              key={note.id}
                              className="flex gap-3 bg-gray-50 rounded-lg p-3"
                            >
                              <div className="flex-1">
                                <p className="text-sm text-gray-700">{note.note}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(note.created_at).toLocaleString()}
                                  {note.created_by_name && ` • ${note.created_by_name}`}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeNote(note.id)}
                                className="p-1 hover:bg-red-50 rounded transition-colors"
                              >
                                <XIcon className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          ))
                      ) : (
                        <p className="text-sm text-gray-500 italic">No notes yet</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:rounded-xl lg:border lg:border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/leads')}
              className={mobile.secondaryBtn}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={mobile.primaryBtn}
            >
              <PlusIcon className="w-5 h-5" />
              <span className="sm:hidden">{loading ? 'Saving...' : 'Save'}</span>
              <span className="hidden sm:inline">{loading ? 'Updating...' : 'Update Lead'}</span>
            </button>
          </div>
        </form>

      <ConvertLeadModal
        lead={buildConvertLeadSnapshot()}
        open={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        onSuccess={async (customerId) => {
          setShowConvertModal(false)
          setNotification({
            message: 'Lead converted to customer successfully',
            type: 'success',
            customerId,
          })
        }}
        onError={(message) => {
          setNotification({ message, type: 'error' })
        }}
        serviceCategories={serviceCategories}
      />

      {/* Notification Popup Modal */}
      {notification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl ${
              notification.type === 'success'
                ? 'border-2 border-green-500'
                : 'border-2 border-red-500'
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                  notification.type === 'success'
                    ? 'bg-green-100'
                    : 'bg-red-100'
                }`}
              >
                {notification.type === 'success' ? (
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p
                  className={`text-lg font-semibold ${
                    notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {notification.message}
                </p>
                {notification.customerId && (
                  <button
                    type="button"
                    onClick={() => navigate(`/customers/view/${notification.customerId}`)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    View Customer
                  </button>
                )}
              </div>
              <button
                onClick={() => setNotification(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}